import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Button,
} from "@mui/material";
import TvIcon from "@mui/icons-material/Tv";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";

function routeFor(screen) {
  // Use DB PK id for routing, Station ID is just a logical ID
  return `/${screen.role}/${screen.stationId}`;
}

function readyScreenRouteFor(sublocationId, path) {
  if (sublocationId == null || sublocationId === "") return null;
  return `/${sublocationId}${path}`;
}

// Role → badge style map
const ROLE_BADGE_STYLES = {
  expo: { label: "EXPO", color: "#1e88e5", bg: "rgba(30,136,229,0.12)" },
  prep: { label: "PREP", color: "#43a047", bg: "rgba(67,160,71,0.12)" },
  runner: { label: "RUNNER", color: "#8e24aa", bg: "rgba(142,36,170,0.12)" },
  mirror: { label: "MIRROR", color: "#546e7a", bg: "rgba(84,110,122,0.12)" },
};

function getRoleBadge(role) {
  if (!role) return null;
  const key = role.toLowerCase();
  return (
    ROLE_BADGE_STYLES[key] || {
      label: role.toUpperCase(),
      color: "#374151",
      bg: "rgba(55,65,81,0.08)",
    }
  );
}

function ScreenCard({
  label,
  stationId,
  hierarchyLabel,
  onClick,
  icon,
  badge,
}) {
  return (
    <Card
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        transition: "0.25s ease",
        textAlign: "center",
        "&:hover": {
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          transform: "translateY(-4px)",
        },
      }}>
      {badge && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: badge.color,
            backgroundColor: badge.bg,
          }}>
          {badge.label}
        </Box>
      )}

      <CardActionArea
        onClick={onClick}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: 2,
            minHeight: 200,
          }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              background:
                "radial-gradient(circle at 30% 20%, #e8f0fe, #d2e3fc)",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2.5,
            }}>
            {icon || <TvIcon sx={{ fontSize: 32, color: "#4285f4" }} />}
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              px: 1,
              textAlign: "center",
              wordBreak: "break-word",
            }}>
            {label}
          </Typography>

          {hierarchyLabel && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "text.secondary",
              }}>
              {hierarchyLabel}
            </Typography>
          )}

          {stationId != null && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: "text.secondary" }}>
              Station ID: {stationId}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/**
 * A row of cards where every card has the same width.
 */
function ScreenRow({ items, renderItem, keyField = "id" }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
      }}>
      {items.map((item) => {
        const key = item[keyField] ?? item.key;
        return (
          <Box
            key={key}
            sx={{
              width: 220,
              flex: "0 0 220px",
            }}>
            {renderItem(item)}
          </Box>
        );
      })}
    </Box>
  );
}

// Role scheme:
// - screens:all
// - screens:site:<SITE_ID>
// - screens:location:<LOCATION_ID>
// - screens:sublocation:<SUBLOCATION_ID>
function getScreenScopes(roles = []) {
  const scopes = {
    all: false,
    siteIds: new Set(),
    locationIds: new Set(),
    sublocationIds: new Set(),
  };

  for (const raw of roles) {
    const role = String(raw || "").trim();

    if (role === "superadmin" || role === "screens:all") scopes.all = true;

    const siteMatch = role.match(/^screens:site:(\d+)$/);
    if (siteMatch) scopes.siteIds.add(Number(siteMatch[1]));

    const locMatch = role.match(/^screens:location:(\d+)$/);
    if (locMatch) scopes.locationIds.add(Number(locMatch[1]));

    const subMatch = role.match(/^screens:sublocation:(\d+)$/);
    if (subMatch) scopes.sublocationIds.add(Number(subMatch[1]));
  }

  return scopes;
}

function formatNameWithId(label, id) {
  const cleanId = id != null && id !== "" ? String(id) : "Unassigned";
  return `${label} (ID: ${cleanId})`;
}

function getLoggedInDisplay(user = {}) {
  return (
    user?.name ||
    user?.preferred_username ||
    user?.email ||
    user?.given_name ||
    "Unknown User"
  );
}

function sortByNumericIdAsc(items = [], idKey) {
  return [...items].sort((a, b) => {
    const aId = a?.[idKey];
    const bId = b?.[idKey];
    const aNum = Number(aId);
    const bNum = Number(bId);
    const aValid = Number.isFinite(aNum);
    const bValid = Number.isFinite(bNum);

    if (aValid && bValid) return aNum - bNum;
    if (aValid) return -1;
    if (bValid) return 1;
    return String(aId ?? "").localeCompare(String(bId ?? ""));
  });
}

export default function ScreensSelection() {
  const navigate = useNavigate();
  const [screens, setScreens] = useState([]);
  const { auth, logout } = useAuth();
  const loggedInAs = getLoggedInDisplay(auth.user);
  const userHandle =
    auth.user?.preferred_username || auth.user?.email || auth.user?.sub || "";

  const handleLogout = async () => {
    try {
      if (window._socket?.connected) window._socket.disconnect();
      await logout({ redirectUri: window.location.origin });
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };

  useEffect(() => {
    async function load() {
      try {
        const res = await apiFetch("/updates-reports/stations");
        const data = await res.json();

        const mapped = data.map((s) => ({
          id: s.id, // Unit ID / DB PK
          stationId: s.station_id, // logical Station ID
          role: s.role,
          name: s.station_name || s.name || "",

          siteId: s.site_id,
          locationId: s.location_id,
          sublocationId: s.sublocation_id,

          siteName: s.site_name || "Unassigned Site",
          locationName: s.location_name || "Unassigned Location",
          sublocationName: s.sublocation_name || "Unassigned Sublocation",
        }));

        setScreens(mapped);
      } catch (err) {
        console.error("❌ Failed to fetch screens:", err);
        setScreens([]);
      }
    }
    load();
  }, []);

  const scopes = useMemo(() => getScreenScopes(auth.roles || []), [auth.roles]);

  const visibleScreens = useMemo(() => {
    if (scopes.all) return screens;

    return screens.filter((s) => {
      const siteOk = s.siteId != null && scopes.siteIds.has(Number(s.siteId));
      const locOk =
        s.locationId != null && scopes.locationIds.has(Number(s.locationId));
      const subOk =
        s.sublocationId != null &&
        scopes.sublocationIds.has(Number(s.sublocationId));
      return siteOk || locOk || subOk;
    });
  }, [screens, scopes]);

  // Group by Site → Location → Sublocation (use ID as key to avoid name collisions)
  const groupedScreens = useMemo(() => {
    const result = {};

    visibleScreens.forEach((screen) => {
      const siteIdKey =
        screen.siteId != null ? `site:${screen.siteId}` : "site:unassigned";
      const locIdKey =
        screen.locationId != null
          ? `loc:${screen.locationId}`
          : "loc:unassigned";
      const subIdKey =
        screen.sublocationId != null
          ? `sub:${screen.sublocationId}`
          : "sub:unassigned";

      if (!result[siteIdKey]) {
        result[siteIdKey] = {
          siteId: screen.siteId ?? null,
          siteName: screen.siteName || "Unassigned Site",
          locations: {},
        };
      }

      if (!result[siteIdKey].locations[locIdKey]) {
        result[siteIdKey].locations[locIdKey] = {
          locationId: screen.locationId ?? null,
          locationName: screen.locationName || "Unassigned Location",
          sublocations: {},
        };
      }

      if (!result[siteIdKey].locations[locIdKey].sublocations[subIdKey]) {
        result[siteIdKey].locations[locIdKey].sublocations[subIdKey] = {
          sublocationId: screen.sublocationId ?? null,
          sublocationName: screen.sublocationName || "Unassigned Sublocation",
          items: [],
        };
      }

      result[siteIdKey].locations[locIdKey].sublocations[subIdKey].items.push(
        screen
      );
    });

    // Sort each sublocation array by Station ID
    Object.values(result).forEach((siteObj) => {
      Object.values(siteObj.locations).forEach((locObj) => {
        Object.values(locObj.sublocations).forEach((subObj) => {
          subObj.items.sort(
            (a, b) => Number(a.stationId || 0) - Number(b.stationId || 0)
          );
        });
      });
    });

    return result;
  }, [visibleScreens]);

  const staticScreens = [];

  const sublocationScreens = [
    { id: "queue", label: "Queue Screen", path: "/queuescreen" },
    {
      id: "mobile-queue",
      label: "Mobile Queue Screen",
      path: "/mobilequeuescreen",
    },
    { id: "ready", label: "Ready Screen", path: "/readyscreen" },
    {
      id: "readysettings",
      label: "Ready Screen Settings",
      path: "/readyscreensettings",
    },
  ];

  const hasScopeRoles = useMemo(() => {
    if (scopes.all) return true;
    return (
      scopes.siteIds.size > 0 ||
      scopes.locationIds.size > 0 ||
      scopes.sublocationIds.size > 0
    );
  }, [scopes]);

  return (
    <Box
      sx={{
        width: "100%",
        py: 6,
        px: 3,
        display: "flex",
        justifyContent: "center",
        background: "linear-gradient(to bottom right, #eff6ff, #ffffff)",
      }}>
      <Box sx={{ width: "100%", maxWidth: "1400px" }}>
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            mb: 6,
            minHeight: { xs: "auto", md: 180 },
            pr: { xs: 0, md: 42 },
          }}
        >
          <Card
            sx={{
              position: { xs: "static", md: "absolute" },
              top: -8,
              right: 0,
              width: { xs: "100%", md: 300 },
              mb: { xs: 3, md: 0 },
              borderRadius: 3,
              textAlign: "left",
              background:
                "linear-gradient(135deg, rgba(66,133,244,0.08), rgba(255,255,255,0.96))",
              boxShadow: "0 8px 24px rgba(66,133,244,0.08)",
            }}
          >
            <CardContent sx={{ px: 3, py: 2.5 }}>
              <Typography
                variant="overline"
                sx={{ color: "#4285f4", fontWeight: 700, letterSpacing: 1 }}
              >
                Logged in as
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 700 }}>
                {loggedInAs}
              </Typography>
            {userHandle && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                {userHandle}
              </Typography>
            )}
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleLogout}
              sx={{ mt: 2, fontWeight: 700 }}
            >
              Log Out
            </Button>
          </CardContent>
        </Card>

          <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 1,
              gap: 1,
            }}>
            <DashboardCustomizeIcon sx={{ fontSize: 32, color: "#4285f4" }} />
            <Typography variant="h4" fontWeight={700}>
              Screen Stations
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{ color: "text.secondary", mt: 1, maxWidth: 520, mx: "auto" }}>
            Launch any kitchen station or system screen with a single click.
          </Typography>

          <Divider
            sx={{
              mt: 3,
              width: 80,
              mx: "auto",
              borderColor: "#4285f4",
            }}
          />
          </Box>
        </Box>

        {/* Dynamic KDS stations */}
        <Box sx={{ mb: 5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 2,
              px: 0.5,
            }}>
            <Typography variant="h6" fontWeight={700}>
              KDS Stations
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}>
              Grouped by Site / Location / Sublocation (with IDs), sorted by
              Station ID
            </Typography>
          </Box>

          {visibleScreens.length === 0 ? (
            <Box
              sx={{
                py: 4,
                px: 3,
                textAlign: "center",
                borderRadius: 3,
                border: "1px dashed rgba(0,0,0,0.12)",
                backgroundColor: "rgba(255,255,255,0.7)",
              }}>
              <Typography variant="body1" fontWeight={500}>
                {hasScopeRoles
                  ? "No stations found for your assigned scope."
                  : "No station scope roles assigned."}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}>
                {hasScopeRoles
                  ? "If this seems wrong, check your site/location/sublocation permissions."
                  : "Assign one of: screens:all, screens:site:<id>, screens:location:<id>, screens:sublocation:<id>."}
              </Typography>
            </Box>
          ) : (
            sortByNumericIdAsc(Object.values(groupedScreens), "siteId").map((siteObj) => (
              <Box
                key={`site:${siteObj.siteId ?? "unassigned"}`}
                sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                  Site: {formatNameWithId(siteObj.siteName, siteObj.siteId)}
                </Typography>

                {sortByNumericIdAsc(Object.values(siteObj.locations), "locationId").map((locObj) => (
                  <Box
                    key={`loc:${locObj.locationId ?? "unassigned"}`}
                    sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ mb: 1, color: "text.primary" }}>
                      Location:{" "}
                      {formatNameWithId(locObj.locationName, locObj.locationId)}
                    </Typography>

                    {sortByNumericIdAsc(Object.values(locObj.sublocations), "sublocationId").map((subObj) => (
                      <Box
                        key={`sub:${subObj.sublocationId ?? "unassigned"}`}
                        sx={{ mb: 2, ml: 2 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 1, color: "text.secondary" }}>
                          SubLocation:{" "}
                          {formatNameWithId(
                            subObj.sublocationName,
                            subObj.sublocationId
                          )}
                        </Typography>

                        <ScreenRow
                          items={subObj.items}
                          renderItem={(screen) => (
                            <ScreenCard
                              label={
                                screen.name ||
                                `${screen.role} ${screen.stationId}`
                              }
                              stationId={screen.stationId}
                              onClick={() => navigate(routeFor(screen))}
                              badge={getRoleBadge(screen.role)}
                            />
                          )}
                        />

                        {subObj.sublocationId != null && (
                          <Box sx={{ mt: 2 }}>
                            <ScreenRow
                              items={sublocationScreens}
                              keyField="id"
                              renderItem={(s) => (
                                <ScreenCard
                                  label={s.label}
                                  onClick={() =>
                                    navigate(
                                      readyScreenRouteFor(subObj.sublocationId, s.path)
                                    )
                                  }
                                  hierarchyLabel={`${subObj.sublocationName} (ID: ${subObj.sublocationId})`}
                                  badge={{
                                    label: "SUBLOCATION",
                                    color: "#92400e",
                                    bg: "rgba(146,64,14,0.12)",
                                  }}
                                />
                              )}
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>

        {/* Static / system screens */}
        {staticScreens.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 2,
                px: 0.5,
              }}>
              <Typography variant="h6" fontWeight={700}>
                Misc Screens
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Additional Features
              </Typography>
            </Box>

            <ScreenRow
              items={staticScreens}
              keyField="id"
              renderItem={(s) => (
                <ScreenCard
                  label={s.label}
                  onClick={() => navigate(s.path)}
                  hierarchyLabel="System Screen"
                  badge={{
                    label: "SYSTEM",
                    color: "#92400e",
                    bg: "rgba(146,64,14,0.12)",
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
